import {
  Box,
  Container,
  Stack,
  Text,
  Heading,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import t from "../translations";

const MotionBox = motion(Box);

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
  }),
};

export default function Hero({ color }) {
  const subtitleColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Container maxW="4xl" id="hero">
      <Stack
        as={Box}
        textAlign="center"
        spacing={{ base: 4, md: 8 }}
        pt={{ base: 24, md: 44 }}
        pb={{ base: 16, md: 32 }}
      >
        <MotionBox
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          <Text fontSize={{ base: "lg", md: "xl" }} color={subtitleColor}>
            {t.hero.greeting}
          </Text>
        </MotionBox>

        <MotionBox
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          <Heading
            fontWeight={800}
            fontSize={{ base: "3xl", sm: "4xl", md: "6xl" }}
            lineHeight="shorter"
          >
            {t.hero.name}
          </Heading>
        </MotionBox>

        <MotionBox
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={2}
        >
          <Heading
            fontWeight={600}
            fontSize={{ base: "lg", sm: "xl", md: "3xl" }}
            color={`${color}.400`}
            minH={{ base: "28px", sm: "36px", md: "48px" }}
          >
            <TypeAnimation
              sequence={t.hero.roles.flatMap((role) => [role, 2500])}
              wrapper="span"
              speed={50}
              repeat={Infinity}
              style={{ color: "inherit" }}
            />
          </Heading>
        </MotionBox>

        <MotionBox
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={3}
        >
          <Text
            color={subtitleColor}
            fontSize={{ base: "md", sm: "lg", md: "xl" }}
            maxW="2xl"
            mx="auto"
            lineHeight="tall"
          >
            {t.hero.description}
          </Text>
        </MotionBox>

        <MotionBox
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={4}
        >
          <Stack
            direction={{ base: "column", sm: "row" }}
            spacing={{ base: 3, sm: 4 }}
            justify="center"
            align="center"
            pt={4}
          >
            <Button
              colorScheme={color}
              size={{ base: "md", md: "lg" }}
              rounded="full"
              px={{ base: 6, md: 8 }}
              w={{ base: "full", sm: "auto" }}
              onClick={() =>
                document
                  .getElementById("experience")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
              transition="all 0.2s"
            >
              {t.hero.primaryButton}
            </Button>
            <Button
              variant="outline"
              colorScheme={color}
              size={{ base: "md", md: "lg" }}
              rounded="full"
              px={{ base: 6, md: 8 }}
              w={{ base: "full", sm: "auto" }}
              onClick={() =>
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
              transition="all 0.2s"
            >
              {t.hero.secondaryButton}
            </Button>
          </Stack>
        </MotionBox>
      </Stack>
    </Container>
  );
}
